import React, {useContext} from "react";
import PropTypes from "prop-types";
import {withNamespaces} from "react-i18next";
import CancelForm from "../models/CancelForm";
import PaymentFormComponent from "../../payments/components/PaymentForm/PaymentForm.component";
import {AuthContext} from "../../member/components/AuthContext";
import MoneyHelper from "../../../services/MoneyHelper";
import {CancellationProcessContext} from "./CancellationProcess.component";
import InputGroup from "../../forms/components/InputGroup/InputGroup";
import {CancellationPaymentTypes, CancellationPeriodType} from "../models/CancelDetails";
import DateHelper from "../../../services/DateHelper";

const CooldownPeriodNoticeComponent = withNamespaces()((props) => {
  const {member, cancelDetails, t} = props;
  return (
    <p className='light'>
      <span
        className='block mb-8'>{t('membership_cancellation_cancel_form_cooldown_period_line1', {cooldown_period: cancelDetails.cancellation.getCooldownDays()})}</span>
      <span
        className='block'>{t('membership_cancellation_cancel_form_cooldown_period_membership_start', {date: member.getFormattedStart()})}</span>
      <span
        className='block mb-8'>{t('membership_cancellation_cancel_form_cooldown_period_cooldown_end', {date: cancelDetails.cancellation.formattedCooldownEnd(member.membership_start)})}</span>
      <span className='block'>{t('membership_cancellation_cancel_form_cooldown_period_line2')}</span>
    </p>
  );
});

CooldownPeriodNoticeComponent.propTypes = {
  member: PropTypes.object.isRequired,
  cancelDetails: PropTypes.object.isRequired,
};

const MinimumPeriodNoticeComponent = withNamespaces()((props) => {
  const {member, cancelDetails, t} = props;

  return (
    <p className='light'>
      <span className='block bold'>{t('membership_cancellation_cancel_form_minimum_period_line1')}</span>

      {cancelDetails && cancelDetails.cancellation.hasBuyoutFee() ? (
        <span className='block mbt-8'>
          {t('membership_cancellation_buyout_notice', {buyout_price: MoneyHelper.toPrice(cancelDetails.cancellation.getBuyoutPrice())})}
        </span>
      ) : (
        <span className='block mbt-8'>{t('membership_cancellation_cancel_form_minimum_period_line2', {
          club_name: member.club.name,
          minimum_period: cancelDetails.cancellation.getMandatoryDays()
        })}</span>
      )}
    </p>
  );
});

MinimumPeriodNoticeComponent.propTypes = {
  member: PropTypes.object.isRequired,
  cancelDetails: PropTypes.object.isRequired,
};

const NoticePeriodNoticeComponent = withNamespaces()((props) => {
  const {member, cancelDetails, t} = props;
  return (
    <p className='light'>
      <span
        className='block bold'>{t(`membership_cancellation_cancel_form_notice_period_${member.mms_api}_line1`)}</span>
      {cancelDetails && cancelDetails.cancellation.hasBuyoutFee() ? (
          <span className='block mbt-8'>{t('membership_cancellation_buyout_notice', {buyout_price: cancelDetails.cancellation.getBuyoutPrice()})}</span>
      ) : (
        <span className='block mbt-8'>{t(`membership_cancellation_cancel_form_notice_period_${member.mms_api}_line2`, {
          club_name: member.club.name,
          notice_period: cancelDetails.cancellation.getNoticeDays()
        })}</span>
      )}
    </p>
  );
});

NoticePeriodNoticeComponent.propTypes = {
  member: PropTypes.object.isRequired,
  cancelDetails: PropTypes.object.isRequired,
};


const CancellationFormComponent = (props) => {
  const {t} = props;
  const {cancelDetails} = useContext(CancellationProcessContext);
  const {member} = useContext(AuthContext);
  const cancellationData = props.data.cancellation || new CancelForm();

  const handleChange = (name, value) => {
    const data = new CancelForm({
      ...cancellationData,
      [name]: value,
    });
    props.setData({cancellation: data});
  };

  const textComponents = {
    [CancellationPeriodType.CooldownPeriod]: CooldownPeriodNoticeComponent,
    [CancellationPeriodType.MandatoryPeriod]: MinimumPeriodNoticeComponent,
    [CancellationPeriodType.NoticePeriod]: NoticePeriodNoticeComponent
  }
  const TextComponent = cancelDetails && textComponents[cancelDetails.cancellation.type];

  const isCooldown = cancelDetails && cancelDetails.cancellation.isCooldownPeriod();
  const hasToPay = !isCooldown && cancelDetails.cancellation.hasDues();

  const hasPaymentTypeSelection = cancelDetails && cancelDetails.cancellation.allow_regular_payment;

  console.log('CancellationForm', member, cancelDetails, cancellationData);
  return (
    <div className="cancellation-form-container">
      <h2>{t('membership_cancellation_cancel_form_header')}</h2>

      <div className={"cancellation-form-container-notices"}>
        {TextComponent && (
          <TextComponent cancelDetails={cancelDetails} member={member}/>
        )}

        {cancelDetails && cancelDetails.cancellation.hasRecurringServices() && (
          <div dangerouslySetInnerHTML={{ __html: t('membership_cancellation_cancel_additional_services_warning') }} />
        )}

        {cancelDetails.cancellation.hasPendingPayment() && (
          <div className="pending-notice" dangerouslySetInnerHTML={{ __html: t('membership_cancellation_cancel_pending_payment_warning') }} />
        )}
      </div>

      <div className='cancellation-summary'>
        <h3>{t('membership_cancellation_cancel_form_review_summary')}</h3>
        <div className='form-group'>
          <div className='form-col margin-right'>
            <h4>{t(`membership_cancellation_cancel_form_membership_details`)}</h4>
            {[
              {name: 'membership_status', value: member.membership_status},
              {name: 'membership_type', value: member.membership_type},
              {name: 'home_club', value: member.club.name},
              {name: 'payment_type', value: member.getPaymentInterval()},
            ].filter(a => !!a).map((item, index) => (
              <p key={index} className='light'>
                <span className='bold'>{t(`membership_cancellation_cancel_form_${item.name}`)}</span> {item.value}
              </p>
            ))}
            {!isCooldown && (
              <React.Fragment>
                {cancelDetails.cancellation.getInvoiceToPay().length > 0 ? (
                  <p className='light invoice'>
                    <span className='bold'>{t(`membership_cancellation_cancel_form_next_dues_invoices`)}</span>
                    <span>
                      {cancelDetails.cancellation.getInvoiceToPay().map((invoice, index) => (
                          <React.Fragment key={index}>
                            <span>{DateHelper.formatDate(invoice.due_date)} - {MoneyHelper.toPrice(invoice.amount_due)}</span><br/>
                          </React.Fragment>
                      ))}
                      {(cancelDetails.cancellation.buyout_conditions ? (cancelDetails.cancellation.buyout_conditions.discounted_invoices || []) : [])
                        .map((invoice, index) => (
                          <React.Fragment key={index}>
                            <span className='strike-through'>{DateHelper.formatDate(invoice.due_date)} - {MoneyHelper.toPrice(invoice.amount_due)}</span><br/>
                          </React.Fragment>
                        ))}
                    </span>
                  </p>
                ) : (
                  <React.Fragment>
                    <p className='light'>
                      <span
                        className='bold'>{t(`membership_cancellation_cancel_form_next_dues_date`)}</span> {member.plan_info ? member.plan_info.getFormattedNextDueDate() : ''}
                    </p>
                    <p className='light'>
                      <span
                        className='bold'>{t(`membership_cancellation_cancel_form_next_dues`)}</span> {member.plan_info ? MoneyHelper.toPrice(member.plan_info.nextDue) : ''}
                    </p>
                  </React.Fragment>
                )}
                <p className='light'>
                  <span
                    className='bold'>{t(`membership_cancellation_cancel_form_active_until`)}</span> {cancelDetails.cancellation.formattedActiveUntil()}
                </p>
              </React.Fragment>
            )}
          </div>

          <div className='form-col'>
            {!isCooldown && (
              <React.Fragment>
                <h4>{t(`membership_cancellation_cancel_form_amount_due`)}</h4>
                {(
                  [
                    {
                      name: 'outstanding_balance',
                      value: cancelDetails && cancelDetails.cancellation['outstanding_balance'] ? cancelDetails.cancellation['outstanding_balance'] : 0
                    },
                    {
                      name: 'annual_fee',
                      value: cancelDetails && cancelDetails.cancellation['annual_fee'] ? cancelDetails.cancellation['annual_fee'] : 0
                    },
                    {name: 'dues_payment', value: cancelDetails ? cancelDetails.cancellation.getDuesPayment() : 0},
                    {
                      name: 'processing_fee_payment',
                      value: cancelDetails.cancellation.getProcessingFeeAmount(cancellationData.isPayNow())
                    },
                    {name: 'buyout_price', value: cancelDetails.cancellation.getBuyoutPrice()},
                    {
                      name: 'total_amount',
                      value: cancelDetails ? cancelDetails.cancellation.getFinalAmount(cancellationData.isPayNow()) : 0,
                      isTotal: true
                    },
                  ]
                    .filter(item => item.value > 0)
                    .map(item => ({
                      name: item.name,
                      value: MoneyHelper.toPrice(item.value)
                    })).concat([]) || []
                ).map((item, index) => (
                  <p key={index} className='light'>
                    <span
                      className={`bold ${item.isTotal ? 'total' : ''}`}>{t(`membership_cancellation_cancel_form_${item.name}`)}</span> {item.value}
                  </p>
                ))}
              </React.Fragment>
            )}
          </div>
        </div>
      </div>

      {!isCooldown && (
        <p className='light'>{t('membership_cancellation_cancel_gym_use_notice')}</p>
      )}

      {hasPaymentTypeSelection && (
        <div className='payment-form-container'>
          <h3>{t('membership_cancellation_cancel_form_payment_type_selection')}</h3>
          {Object.keys(CancellationPaymentTypes).map((key, index) => (
            <div className='radio' key={index}>
              <input type="radio" name="payment_type" id={`payment-type-${key.toLowerCase()}`}
                     checked={cancellationData.payment_type === CancellationPaymentTypes[key]}
                     onChange={() => handleChange('payment_type', CancellationPaymentTypes[key])}/>
              <label
                htmlFor={`payment-type-${key.toLowerCase()}`}>{props.t(`membership_cancellation_cancel_form_payment_type_${key.toLowerCase()}`)}</label>
            </div>
          ))}
        </div>
      )}

      {hasToPay && (
        <React.Fragment>
          <div
            className={`payment-form-container expandable ${hasPaymentTypeSelection && cancellationData.payment_type !== CancellationPaymentTypes.Now ? 'not-shown' : ''}`}>
            <h3>{t('membership_cancellation_cancel_form_payment_method', {step: hasPaymentTypeSelection ? 3 : 2})}</h3>
            <PaymentFormComponent paymentForm={cancellationData.payment_method} onChange={(method) => {
              handleChange('payment_method', method)
            }} default_card={member.default_card || {}} allowChangeCard={true}/>
          </div>
        </React.Fragment>
      )}

      {!isCooldown && (
        <div className="form-group">
          <div className="checkbox">
            <input
              id={`accept_terms`}
              name={`accept_terms`}
              type="checkbox"
              checked={cancellationData.terms_accepted}
              onChange={() => {
                handleChange('terms_accepted', !cancellationData.terms_accepted)
              }}/>
            <label htmlFor={`accept_terms`}>
              <div dangerouslySetInnerHTML={{
                __html: props.t(`membership_cancellation_cancel_form_terms_conditions`, {notice_period: cancelDetails.cancellation.getNoticeDays()})
              }}/>
            </label>
          </div>
        </div>
      )}

      <div className="form-group">
        <InputGroup id={'cancellation-offer-comment'} label={t('member_cancellation_downgrade_comment_label')}
                    topLabel={true}>
          <input placeholder={t('member_cancellation_downgrade_comment_label')} type="text"
                 value={cancellationData.comment || ''} onChange={e => handleChange('comment', e.target.value)}/>
        </InputGroup>
      </div>

      {/*<div>*/}
      {/*  <button className='form-main-button'*/}
      {/*          disabled={!isValid()}*/}
      {/*          onClick={(e) => cancelMembership()}>*/}
      {/*    {props.t('membership_cancellation_cancel_form_cancel', {price: cancelDetails ? MoneyHelper.toPrice(cancelDetails.member_cancel_details.getFinalAmount()) : ''})}*/}
      {/*  </button>*/}
      {/*</div>*/}
    </div>
  );
};

export default withNamespaces()(CancellationFormComponent);